import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { first } from 'rxjs/operators';
import { ICampaign } from '../../../models/campaign';
import { ISession } from '../../../models/session';
import { CampaignsService, resolveStateClass } from '../../sessions/campaigns.service';
import { AuthService } from '../../auth/auth.service';
import { UsersService } from '../../sessions/users/users.service';
import { ConfirmationModalComponent } from '../../modals/confirmation/confirmation-modal.component';
import { isDateValid, isHtmlEmpty } from '../../../utils/html.utils';
import * as moment from 'moment';
import { merge } from 'ramda';

@Component({
  selector: 'campaigns-list',
  templateUrl: './campaigns.component.html',
  styleUrls: ['./campaigns.component.scss']
})
export class CampaignsComponent implements OnInit, OnDestroy {
  @Input() public session: ISession;
  @Input() public completions: any[] = [];

  public campaigns: ICampaign[] = [];
  public isLoading = false;
  private reloadTimer: any;

  // Pagination
  public currentPage = 1;
  public pageSize = 10;
  public totalPages = 1;
  public total = 0;
  public Math = Math;

  // Sorting
  public sortBy = 'created_at';
  public sortOrder: 'asc' | 'desc' = 'desc';

  // Edit modal
  public showEditModal = false;
  public editingCampaign: ICampaign | null = null;
  public isSaving = false;
  public recipients: {email: string, firstName: string, lastName: string}[] = [];
  public editingTab: 'general' | 'content' | 'jobs' = 'general';

  public activeJob: any = null;
  public isAbortingJob = false;

  constructor(
    private route: ActivatedRoute,
    private campaignsService: CampaignsService,
    private usersService: UsersService,
    private translateService: TranslateService,
    private modalService: BsModalService,
    private authService: AuthService
  ) {}

  ngOnInit() {
    this.loadCampaigns(this.session);
    this.loadRecipients(this.session);
    this.reloadTimer = setInterval(() => this.reloadCampaigns(this.session), 2000);
  }

  ngOnDestroy() {
    clearInterval(this.reloadTimer);
  }

  public loadRecipients(session: ISession) {
    this.usersService.getRegisteredUsers(session.id)
      .subscribe(
        (users: any[]) => {
          this.recipients = (users || [])
            .map(u => ({
              email: u.Email,
              firstName: u.FirstName,
              lastName: u.LastName
            }))
            .filter(r => !!r.email);
        },
        err => toastr.error(this.translateService.instant('action.failed'))
      );
  }

  public reloadCampaigns(session: ISession) {
    return this.campaignsService
      .getBySession(session, this.currentPage, this.pageSize, this.sortBy, this.sortOrder)
      .subscribe(
        (res: any) => {
          this.campaigns = res.data;
          this.total = res.meta.total;
          this.totalPages = res.meta.totalPages;
          const activeJobs: any[] = res.meta.activeJobs || [];
          const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
          const sessionActiveJob = activeJobs.find(j =>
            j.session_id === +this.session.id &&
            j.job_queued_at && new Date(j.job_queued_at) < thirtyMinutesAgo
          ) || null;
          this.activeJob = sessionActiveJob;
        });
  }

  public loadCampaigns(session: ISession = this.session) {
    this.isLoading = true;
    this.reloadCampaigns(session).add(() => this.isLoading = false);
  }

  public sort(column: string) {
    if (this.sortBy === column) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = column;
      this.sortOrder = 'asc';
    }
    this.currentPage = 1;
    this.loadCampaigns();
  }

  public goToPage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.loadCampaigns();
  }

  public openEditModal(campaign: ICampaign) {
    this.editingTab = 'general';
    this.showEditModal = true;
    this.campaignsService.getById(this.session, campaign.id).subscribe(
      full => { 
        this.editingCampaign = full; 
      },
      _err => {
        this.editingCampaign = { ...campaign };
        toastr.error(this.translateService.instant('action.failed'));
      }
    );
  }

  public onEditCampaignContent(campaign: ICampaign) {
    this.editingCampaign = { 
      ...this.editingCampaign, 
      email_from: campaign.email_from, 
      email_subject: campaign.email_subject, 
      email_content: campaign.email_content 
    } as ICampaign; 
  }

  public duplicateCampaign(campaign: ICampaign) {
    this.editingTab = 'general';
    this.editingCampaign = {
      session_id: +this.session.id,
      name: campaign.name + ' - copy',
      mode: campaign.mode,
      mailing_list: campaign.mailing_list ? [...campaign.mailing_list] : [],
      segment_id: campaign.segment_id,
      scheduled_at: campaign.scheduled_at,
      email_subject: campaign.email_subject,
      email_from: campaign.email_from,
      email_content: campaign.email_content,
    };
    this.showEditModal = true;
  }

  public openCreateModal() {
    this.editingCampaign = {
      session_id: +this.session.id,
      name: '',
      mode: 'immediate',
      mailing_list: [],
      segment_id: 0,
      scheduled_at: null
    };
    this.editingTab = 'general';
    this.showEditModal = true;
  }

  public closeEditModal() {
    this.showEditModal = false;
    this.editingCampaign = null;
  }

  public saveCampaign(dryrun: boolean = true) {
    if (!this.editingCampaign.name || !this.editingCampaign.mode) return;
    this.isSaving = true;

    const { name, mode, recipient_mode, mailing_list, segment_id, scheduled_at, email_from, email_subject, email_content } = this.editingCampaign;
    // The date picker emits browser-local time without offset, send an absolute UTC instant instead
    const payload = {
      name, mode, recipient_mode, mailing_list, segment_id,
      scheduled_at: scheduled_at ? moment(scheduled_at).toISOString() : scheduled_at,
      email_from, email_subject, email_content
    };

    const request$ = this.editingCampaign.id
      ? this.campaignsService.update(this.session, this.editingCampaign.id, payload as ICampaign, dryrun)
      : this.campaignsService.create(this.session, payload as ICampaign, dryrun);

    request$.subscribe(
      () => {
        this.isSaving = false;
        this.closeEditModal();
        this.loadCampaigns();
      },
      err => {
        this.isSaving = false;
      }
    );
  }

  public isCancelable(campaign: ICampaign): boolean {
    return ['queued', 'sending', 'send_pending', 'scheduled'].indexOf(campaign.state) !== -1;
  }

  public cancelCampaign(campaign: ICampaign) {
    const modal = this.modalService.show(ConfirmationModalComponent);
    (modal.content as ConfirmationModalComponent).showConfirmationModal(
      this.translateService.instant('sessions.campaigns.cancel_confirm')
    );
    (modal.content as ConfirmationModalComponent).onClose
      .pipe(first())
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.campaignsService.cancel(this.session, campaign.id).subscribe(
          () => this.loadCampaigns(),
          _err => toastr.error(this.translateService.instant('action.failed'))
        );
      });
  }

  public abortActiveJob() {
    if (!this.activeJob || this.isAbortingJob) return;
    this.isAbortingJob = true;
    this.campaignsService.cancel(this.session, this.activeJob.campaign_id).subscribe(
      () => {
        this.isAbortingJob = false;
        this.activeJob = null;
        this.loadCampaigns();
      },
      _err => {
        this.isAbortingJob = false;
        toastr.error(this.translateService.instant('action.failed'));
      }
    );
  }

  public deleteCampaign(campaign: ICampaign) {
    const modal = this.modalService.show(ConfirmationModalComponent);
    (modal.content as ConfirmationModalComponent).showConfirmationModal(
      this.translateService.instant('sessions.campaigns.delete_confirm')
    );

    (modal.content as ConfirmationModalComponent).onClose
      .pipe(first())
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.campaignsService.delete(this.session, campaign.id).subscribe(
          () => {
            if (this.campaigns.length === 1 && this.currentPage > 1) {
              this.currentPage--;
            }
            this.reloadCampaigns(this.session);
          }
        );
      });
  }

  public getSortIcon(column: string): string {
    if (this.sortBy !== column) return 'fa-sort';
    return this.sortOrder === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc';
  }

  public getPages(): number[] {
    const pages = [];
    for (let i = 1; i <= this.totalPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  public resolveStateClass(campaign: ICampaign): string {
    return resolveStateClass(campaign.state);
  }

  public canSaveCampaign(campaign: ICampaign, dryrun: boolean = true): boolean {
    if (!campaign) return false;
    if (!campaign.name || !campaign.mode) return false;
    if (campaign.mode === 'scheduled' && !isDateValid(campaign.scheduled_at)) return false;
    if (campaign.mode === 'scheduled' && new Date(campaign.scheduled_at || '') < new Date()) return false;
    if (dryrun) return true;
    if (!campaign.email_from || !campaign.email_subject || isHtmlEmpty(campaign.email_content)) return false;
    if (campaign.recipient_mode === 'segment' && (!campaign.segment_id || campaign.segment_id <= 0)) return false;
    if (campaign.recipient_mode === 'email_list' && (!campaign.mailing_list || campaign.mailing_list.length === 0)) return false;
    return true;
  }

  public formatDate(date: Date): string {
    if (!date) {
      return '-';
    } 
    const lang = this.translateService.store.currentLang || this.translateService.store.defaultLang;
    return lang.toLowerCase().substring(0, 2) === 'fr' ? moment(date).format("DD/MM/YYYY HH:mm") : moment(date).format("MM/DD/YYYY, hh:mm A");
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/email/campaigns/campaigns.component.ts